import React, { useEffect, useMemo, useState } from 'react';

import { Box } from '@chakra-ui/react';
import { map } from 'lodash';

import { ButtonGroup } from 'components/DataEntry';
import { DashboardWidget } from 'components/Visualization/CDashboard/Widget/DashboardWidget';
import { BarChart, LineChart } from 'components/Visualization/Charts';
import { lineChartStylesVulnerabilityDashboard } from 'containers/Visibility/Vulnerabilities/Components/styles';
import {
  vulnerabilityChartItems,
  vulnerabilityChartColorMap,
} from 'containers/Visibility/Vulnerabilities/Components/utils';
import { VulnerabilityChartProps } from 'containers/Visibility/Vulnerabilities/types';

import { formatDate } from '../../../../../../utils/date';

export const HostsWithVulnerabilities = (props: VulnerabilityChartProps) => {
  const {
    showResolved = false,
    cumulative = false,
    severities,
    chartType,
    updateValue,
  } = props;
  const [data, setData] = useState<Record<string, any>[]>([]);

  useEffect(() => {
    setData(
      (() => {
        const totals: Record<string, number> = {};
        return map(severities.data?.critical_resources, (item, date) => {
          const values = {
            Critical: item,
            // Any severity series can be missing from the response.
            High: severities.data?.high_resources?.[date],
            Medium: severities.data?.medium_resources?.[date],
            Low: severities.data?.low_resources?.[date],
            Unknown: severities.data?.unknown_resources?.[date],
          };
          if (cumulative) {
            Object.entries(values).forEach(([severity, value]) => {
              totals[severity] = (totals[severity] ?? 0) + Number(value ?? 0);
              values[severity] = totals[severity];
            });
          }
          return { Date: formatDate(date, 'D-MMM'), ...values };
        });
      })(),
    );
  }, [severities.data, cumulative]);

  const commonFields = useMemo(
    () => ({
      xField: 'Date',
      yField: ['Critical', 'High', 'Medium', 'Low', 'Unknown'],
      data: data,
      isLoading: severities.isLoading,
      colorMap: vulnerabilityChartColorMap,
      styles: {
        ...lineChartStylesVulnerabilityDashboard,
        lineStyles: { Critical: { strokeWidth: 6 }, High: { strokeWidth: 6 } },
      },
    }),
    [data, severities.isLoading],
  );

  return (
    <DashboardWidget
      label={`Hosts with ${showResolved ? 'Resolved' : 'Open'} Vulnerabilities`}
      subHeader="Last 7 Days"
      rightHeader={
        <ButtonGroup
          items={vulnerabilityChartItems}
          value={chartType}
          onClick={updateValue}
          styles={{ button: { size: 'xs' } }}
        />
      }
      content={
        <Box w="full" h="full">
          {chartType?.value === 'line' ? (
            <LineChart {...commonFields} />
          ) : (
            <BarChart {...commonFields} stacked />
          )}
        </Box>
      }
      styles={{
        contentWrapper: { px: 0, pt: 0, pb: 1 },
        rightHeader: { hStack: { w: 'full', h: 'full', align: 'start' } },
      }}
    />
  );
};
